import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MapPin, Truck, Home, Mic, MicOff, Camera, Check, Plus, Trash2, Snowflake } from "lucide-react";
import { toast } from "sonner";
import api, { photoUrl } from "@/lib/api";
import StatusPill from "@/components/StatusPill";

const fmt = (iso) =>
  new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

function useSpeech(onResult) {
  const [supported, setSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState(null);
  const recRef = useRef(null);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const rec = new SR();
    rec.lang = "en-AU";
    rec.continuous = true;
    rec.interimResults = false;
    rec.onresult = (e) => {
      let txt = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        txt += e.results[i][0].transcript + " ";
      }
      onResult(txt.trim());
    };
    rec.onend = () => setListening(false);
    rec.onerror = (e) => {
      setListening(false);
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        setError("Mic permission blocked — allow it in your browser to dictate.");
      }
    };
    recRef.current = rec;
    setSupported(true);
    return () => { try { rec.stop(); } catch { /* ignore */ } };
  }, [onResult]);

  const start = () => {
    setError(null);
    if (!recRef.current) return;
    try { recRef.current.start(); setListening(true); } catch { /* already started */ }
  };
  const stop = () => {
    if (!recRef.current) return;
    try { recRef.current.stop(); } catch { /* ignore */ }
    setListening(false);
  };

  return { supported, listening, start, stop, error };
}

const BigButton = ({ onClick, children, tone = "navy", tid, disabled }) => {
  const styles = {
    navy: "bg-[#181854] hover:bg-[#101038] text-white",
    ice: "bg-[#1F5C91] hover:bg-[#184A76] text-white",  // AA-safe (~5.9:1 on white)
    success: "bg-[#16A34A] hover:bg-[#15803D] text-white",
    ghost: "bg-[#ECEDF6] hover:bg-[#E4E6EF] text-[#181854]",
  };
  const disabledCls = "bg-[#E4E6EF] text-[#6A6E85] cursor-not-allowed";
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={tid}
      className={`w-full py-4 rounded-2xl text-base font-semibold shadow-sm active:scale-[0.98] transition-transform duration-200 ${disabled ? disabledCls : styles[tone]} flex items-center justify-center gap-2`}
    >
      {children}
    </button>
  );
};

export default function TechJobDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [part, setPart] = useState({ name: "", qty: 1, price: 0 });
  const [assetHistory, setAssetHistory] = useState([]);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/jobs/${jobId}`).then((r) => {
      setData(r.data);
      if (r.data.job.asset_id) {
        api.get(`/assets/${r.data.job.asset_id}`).then((res) =>
          setAssetHistory((res.data.history || []).filter((j) => j.id !== jobId).slice(0, 3))
        );
      }
    });
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  const speech = useSpeech((txt) => setNote((n) => (n ? n + " " : "") + txt));

  if (!data) return <div className="p-6 text-[#6A6E85]">Loading…</div>;
  const { job } = data;

  const setStatus = async (s) => {
    try {
      await api.post(`/jobs/${jobId}/status`, { status: s });
      toast.success(`Marked ${s.replace("_", " ").toLowerCase()}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/jobs/${jobId}/notes`, { raw_text: note.trim() });
      setNote("");
      toast.success("Note added & tidied");
      load();
    } catch { toast.error("Failed to add note"); }
  };

  const addPart = async () => {
    if (!part.name.trim()) return;
    await api.post(`/jobs/${jobId}/parts`, {
      name: part.name.trim(),
      qty: Number(part.qty) || 1,
      price: Number(part.price) || 0,
    });
    setPart({ name: "", qty: 1, price: 0 });
    load();
  };

  const removePart = async (i) => {
    await api.delete(`/jobs/${jobId}/parts/${i}`);
    load();
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/jobs/${jobId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo added");
      load();
    } catch { toast.error("Upload failed"); }
    finally { e.target.value = ""; }
  };

  const complete = async () => {
    try {
      await api.post(`/jobs/${jobId}/status`, { status: "COMPLETE" });
      toast.success("Job complete — draft invoice created.");
      navigate("/tech");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not complete");
    }
  };

  const isDone = ["COMPLETE", "INVOICED", "CLOSED"].includes(job.status);

  // Primary sticky action varies by status
  let stickyAction = null;
  if (job.status === "NEW" || job.status === "SCHEDULED") {
    stickyAction = (
      <BigButton onClick={() => setStatus("EN_ROUTE")} tid="tech-onmyway" tone="ice">
        <Truck size={20} /> On my way
      </BigButton>
    );
  } else if (job.status === "EN_ROUTE") {
    stickyAction = (
      <BigButton onClick={() => setStatus("ON_SITE")} tid="tech-arrived" tone="navy">
        <Home size={20} /> Arrived
      </BigButton>
    );
  } else if (job.status === "ON_SITE") {
    stickyAction = (
      <BigButton onClick={complete} tid="tech-complete" tone="success">
        <Check size={22} /> Mark complete
      </BigButton>
    );
  }

  return (
    <div className="p-4 space-y-5 pb-36">
      {/* Job summary */}
      <div className={`bg-white rounded-2xl border border-[#E4E6EF] p-4 shadow-sm status-border-${job.status}`}>
        <div className="flex items-start justify-between gap-2">
          <div>
            <h1 className="text-xl font-bold text-[#14162E] leading-tight">{job.title}</h1>
            <p className="text-sm text-[#6A6E85] mt-1">{job.client_name}</p>
            <p className="text-sm text-[#6A6E85] flex items-center gap-1 mt-1">
              <MapPin size={14} /> {job.site_address}
            </p>
            {job.asset_label ? (
              <p className="text-sm text-[#6A6E85] flex items-center gap-1 mt-1">
                <Snowflake size={13} className="text-[#4F9CE0]" /> {job.asset_label}
                <span className="text-xs">({job.asset_type})</span>
              </p>
            ) : (
              <p className="text-sm text-[#6A6E85] flex items-center gap-1 mt-1">
                <Snowflake size={13} className="text-[#4F9CE0]" /> No asset linked
              </p>
            )}
          </div>
          <StatusPill status={job.status} size="lg" />
        </div>
        <p className="text-xs text-[#6A6E85] mt-3">Scheduled {fmt(job.scheduled_at)}</p>
      </div>

      {/* Previous visits */}
      {assetHistory.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#E4E6EF] p-4 shadow-sm">
          <h2 className="text-xs uppercase tracking-widest text-[#6A6E85] font-semibold mb-2">Previous visits</h2>
          <ul className="space-y-2 text-sm">
            {assetHistory.map((h) => (
              <li key={h.id} className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-[#14162E] truncate">{h.title}</p>
                  <p className="text-xs text-[#6A6E85]">{new Date(h.scheduled_at).toLocaleDateString()}</p>
                </div>
                <StatusPill status={h.status} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Notes with voice */}
      <div className="bg-white rounded-2xl border border-[#E4E6EF] p-4 shadow-sm space-y-3">
        <h2 className="text-xs uppercase tracking-widest text-[#6A6E85] font-semibold">Notes</h2>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={4}
          placeholder="Type or dictate — office notes will be auto-tidied."
          data-testid="tech-note-input"
          className="w-full px-3 py-3 rounded-lg border border-[#E4E6EF] focus:border-[#4F9CE0] outline-none text-base resize-none"
        />
        <div className="grid grid-cols-2 gap-2">
          {speech.supported && (
            speech.listening ? (
              <BigButton onClick={speech.stop} tid="tech-mic-stop" tone="ice">
                <MicOff size={18} /> Stop
              </BigButton>
            ) : (
              <BigButton onClick={speech.start} tid="tech-mic-start" tone="ghost">
                <Mic size={18} /> Dictate
              </BigButton>
            )
          )}
          <BigButton onClick={addNote} tid="tech-note-add" tone="navy" disabled={!note.trim()}>
            <Plus size={18} /> Save note
          </BigButton>
        </div>
        {speech.error && (
          <p className="text-[11px] text-[#DC2626]">{speech.error}</p>
        )}
        {!speech.supported && (
          <p className="text-[11px] text-[#6A6E85]">Voice input needs a modern mobile browser (Chrome / Safari).</p>
        )}
        {data.notes.length > 0 && (
          <ul className="space-y-2 pt-2">
            {data.notes.slice(0, 3).map((n) => (
              <li key={n.id} className="bg-[#F5F6FA] rounded-lg p-3 text-sm text-[#14162E]">
                {n.clean_text}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Parts */}
      <div className="bg-white rounded-2xl border border-[#E4E6EF] p-4 shadow-sm space-y-3">
        <h2 className="text-xs uppercase tracking-widest text-[#6A6E85] font-semibold">Parts used</h2>
        {(job.parts || []).length > 0 && (
          <ul className="space-y-1.5">
            {(job.parts || []).map((p, i) => (
              <li key={i} className="flex items-center justify-between bg-[#F5F6FA] rounded-lg pl-3 pr-1 py-2 text-sm">
                <span className="text-[#14162E]">{p.name} <span className="text-[#6A6E85]">× {p.qty} · ${Number(p.price).toFixed(2)}</span></span>
                <button
                  onClick={() => removePart(i)}
                  aria-label="Remove part"
                  className="w-11 h-11 min-w-[44px] min-h-[44px] rounded-lg flex items-center justify-center text-[#DC2626] hover:bg-[#FEE2E2] transition-colors duration-200"
                >
                  <Trash2 size={18} />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="space-y-2">
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-[#6A6E85]">Part name</label>
            <input
              placeholder="e.g. Fan motor capacitor 5uF"
              value={part.name}
              onChange={(e) => setPart({ ...part, name: e.target.value })}
              data-testid="tech-part-name"
              className="mt-1 w-full px-3 py-3 rounded-lg border border-[#E4E6EF] focus:border-[#4F9CE0] outline-none text-base"
            />
          </div>
          <div className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-4">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-[#6A6E85]">Qty</label>
              <input
                type="number" min="0" step="0.5"
                value={part.qty}
                onChange={(e) => setPart({ ...part, qty: e.target.value })}
                data-testid="tech-part-qty"
                className="mt-1 w-full px-2 py-3 rounded-lg border border-[#E4E6EF] focus:border-[#4F9CE0] outline-none text-base"
              />
            </div>
            <div className="col-span-5">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-[#6A6E85]">Unit price ($)</label>
              <div className="mt-1 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6A6E85]">$</span>
                <input
                  type="number" min="0" step="0.01"
                  value={part.price}
                  onChange={(e) => setPart({ ...part, price: e.target.value })}
                  data-testid="tech-part-price"
                  className="w-full pl-7 pr-2 py-3 rounded-lg border border-[#E4E6EF] focus:border-[#4F9CE0] outline-none text-base"
                />
              </div>
            </div>
            <div className="col-span-3">
              <button
                onClick={addPart}
                data-testid="tech-part-add"
                aria-label="Add part"
                className="w-full h-[48px] min-h-[44px] rounded-lg bg-[#181854] hover:bg-[#101038] text-white flex items-center justify-center font-semibold transition-colors duration-200"
              >
                <Plus size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Photos */}
      <div className="bg-white rounded-2xl border border-[#E4E6EF] p-4 shadow-sm space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-xs uppercase tracking-widest text-[#6A6E85] font-semibold">Photos</h2>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={onPhoto}
            className="hidden"
            data-testid="tech-photo-input"
          />
          <button
            onClick={() => fileRef.current?.click()}
            data-testid="tech-photo-btn"
            className="inline-flex items-center gap-1.5 text-sm text-[#181854] bg-[#ECEDF6] hover:bg-[#E4E6EF] px-3 py-2 rounded-lg font-medium min-h-[44px] transition-colors duration-200"
          >
            <Camera size={16} /> Add
          </button>
        </div>
        {(job.photos || []).length === 0 ? (
          <p className="text-xs text-[#6A6E85]">No photos yet.</p>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {(job.photos || []).map((p) => (
              <img
                key={p.id}
                src={photoUrl(p.id)}
                alt=""
                className="w-full h-24 object-cover rounded-lg border border-[#E4E6EF]"
              />
            ))}
          </div>
        )}
      </div>

      {isDone && (
        <div className="bg-[#DCFCE7] text-[#166534] rounded-2xl p-4 text-center font-semibold">
          Job {job.status.toLowerCase()} — great work.
        </div>
      )}

      {/* Sticky primary action (above the bottom nav) */}
      {stickyAction && (
        <div className="fixed bottom-14 left-0 right-0 max-w-lg mx-auto bg-white border-t border-[#E4E6EF] p-3 z-20 shadow-lg">
          {stickyAction}
        </div>
      )}
    </div>
  );
}
